import './NotificationsList.scss';

import { useCallback, useEffect } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import Box from 'fe-design-base/atoms/Box';
import CircularInfinite from 'fe-design-base/molecules/Loaders/CircularInfinite';
import { OrderedMap } from 'immutable';

import InfiniteScroll from 'components/InfiniteScroll';

import { cxHelpers } from 'util/className';

import ApprovedUserMergeRequest from '../ListItems/alerts/ApprovedUserMergeRequest';
import ClockOutApproval from '../ListItems/alerts/ClockOutApproval';
import DeclinedUserMergeRequest from '../ListItems/alerts/DeclinedUserMergeRequest';
import EarlyClockIn from '../ListItems/alerts/EarlyClockIn';
import ExpiredCertificate from '../ListItems/alerts/ExpiredCertificate';
import HiringJobApplication from '../ListItems/alerts/HiringJobApplication';
import LateClockIn from '../ListItems/alerts/LateClockIn';
import MissedClockOut from '../ListItems/alerts/MissedClockOut';
import MissedWageRate from '../ListItems/alerts/MissedWageRate';
import { EmptyListItem } from '../ListItems/EmptyListItem';
import Availability from '../ListItems/requests/Availability';
import OpenShift from '../ListItems/requests/OpenShift';
import PendingJob from '../ListItems/requests/PendingJob';
import Personal from '../ListItems/requests/Personal';
import TimeOff from '../ListItems/requests/TimeOff';
import Trade from '../ListItems/requests/Trade';

const VIEW_CLASSES = {
  clock_out: MissedClockOut,
  early_clock_in: EarlyClockIn,
  late_clock_in: LateClockIn,
  wage_rate: MissedWageRate,
  job_application: HiringJobApplication,
  clock_out_approval: ClockOutApproval,
  expired_certificate: ExpiredCertificate,
  trade: Trade,
  time_off: TimeOff,
  personal_alert: Personal,
  availability: Availability,
  pending_job: PendingJob,
  open_shift: OpenShift,
  approved_user_merge_request: ApprovedUserMergeRequest,
  declined_user_merge_request: DeclinedUserMergeRequest,
};

const BOX_STYLE = { boxShadow: '0 -20px 20px white' };

const { cxEl } = cxHelpers('NotificationsList');

type NotificationsListProps = {
  notificationType: 'requests' | 'alerts';
  notificationsItems: OrderedMap<string, any>;
  fetchNotifications: (notificationType: string, page: number) => void;
  isFetching: boolean;
  hasMore: boolean;
  getScrollContainer: () => HTMLElement;
  onClose: () => void;
};

const NotificationsList = ({
  notificationType,
  notificationsItems,
  fetchNotifications,
  isFetching,
  hasMore,
  getScrollContainer,
  onClose,
}: NotificationsListProps) => {
  const fetchPage = useCallback(
    (page: number) => {
      fetchNotifications(notificationType, page);
    },
    [fetchNotifications, notificationType]
  );

  useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);

  const renderLoader = useCallback(() => {
    const positionStyle =
      notificationsItems.size === 0 ? { top: '100px' } : { bottom: 0 };

    return (
      <div>
        <Box
          px={8}
          h={24}
          w="100%"
          absolute
          bottom={0}
          bg="white"
          opacity={0.8}
          style={BOX_STYLE}
        />
        <Box pb={8} absolute {...positionStyle} w="100%" opacity={1.0}>
          <CircularInfinite size="medium" variant="light" />
        </Box>
      </div>
    );
  }, [notificationsItems.size]);

  const renderItems = useCallback(
    (type: 'requests' | 'alerts', items: OrderedMap<string, any>) => {
      if (items.size === 0) {
        if (isFetching) {
          return renderLoader();
        }
        return <EmptyListItem type={type} />;
      }
      return (
        <TransitionGroup>
          {items
            .valueSeq()
            .toArray()
            .map((item: any) => {
              const ItemComponent =
                VIEW_CLASSES[item.get('type') as keyof typeof VIEW_CLASSES];

              // Guard against unknown notification types (e.g., deprecated health_alert)
              if (!ItemComponent) {
                return null;
              }

              return (
                <CSSTransition
                  key={item.get('unique_id')}
                  classNames={cxEl('fade')}
                  timeout={{ enter: 700, exit: 700 }}
                >
                  <ItemComponent
                    key={item.get('unique_id')}
                    item={item}
                    closeDrawer={onClose}
                  />
                </CSSTransition>
              );
            })}
        </TransitionGroup>
      );
    },
    [isFetching, onClose, renderLoader]
  );

  return (
    <Box key={notificationType}>
      <InfiniteScroll
        hasMore={hasMore}
        isFetching={isFetching}
        loadMore={fetchPage}
        pageStart={1}
        getScrollParent={getScrollContainer}
      >
        {renderItems(notificationType, notificationsItems)}
      </InfiniteScroll>

      {isFetching && renderLoader()}
    </Box>
  );
};

export default NotificationsList;
